import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { studentAPI } from '../services/api';
import ProjectCard from '../components/student/ProjectCard';
import ProjectDetails from '../components/student/ProjectDetails';

function StudentDashboard({ user, onLogout }) {
    const [projects, setProjects] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');

    const fetchProjects = useCallback(async () => {
        try {
            const response = await studentAPI.getProjects();
            setProjects(response.data);
        } catch (error) {
            toast.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const handleProjectClick = (project) => {
        setSelectedProject(project);
    };

    const handleBackToList = () => {
        setSelectedProject(null);
        fetchProjects(); // Refresh data
    };

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="spinner"></div>
                <p>Loading your projects...</p>
            </div>
        );
    }

    const miniProjectStatus = projects?.miniProject ? (projects.miniProject.status || 'Pending') : 'Not Assigned';
    const majorProjectStatus = projects?.majorProject ? (projects.majorProject.status || 'Pending') : 'Not Assigned';

    return (
        <div className="dashboard-wrapper">
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-brand">
                    <h2>Novantix</h2>
                    <p>Student Portal</p>
                </div>

                <nav className="sidebar-nav">
                    <button
                        className={`nav-item ${activeTab === 'dashboard' && !selectedProject ? 'active' : ''}`}
                        onClick={() => { setActiveTab('dashboard'); setSelectedProject(null); }}
                    >
                        <span className="icon">📊</span> Dashboard
                    </button>
                    <button
                        className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        <span className="icon">👤</span> My Profile
                    </button>
                    <button
                        className={`nav-item ${activeTab === 'resources' ? 'active' : ''}`}
                        onClick={() => setActiveTab('resources')}
                    >
                        <span className="icon">📚</span> Resources
                    </button>
                    <button
                        className={`nav-item ${activeTab === 'help' ? 'active' : ''}`}
                        onClick={() => setActiveTab('help')}
                    >
                        <span className="icon">❓</span> Help & Support
                    </button>
                </nav>

                <div className="sidebar-footer">
                    <div className="user-mini-profile">
                        <div className="avatar">{user.name.charAt(0)}</div>
                        <div className="info">
                            <span className="name">{user.name}</span>
                            <span className="role">{user.category}</span>
                        </div>
                    </div>
                    <button onClick={onLogout} className="btn-logout">
                        <span className="icon">🚪</span> Logout
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                <header className="top-header">
                    <div className="header-title">
                        <h1>{selectedProject ? selectedProject.title : 'Dashboard Overview'}</h1>
                        <p>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-icon">🔔</button>
                    </div>
                </header>

                <div className="content-container">
                    {selectedProject ? (
                        <ProjectDetails
                            project={selectedProject}
                            onBack={handleBackToList}
                            user={user}
                        />
                    ) : (
                        <>
                            {/* Quick Stats */}
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-icon purple">🚀</div>
                                    <div className="stat-info">
                                        <h3>Mini Project</h3>
                                        <p className={`status ${miniProjectStatus.toLowerCase().replace(' ', '-')}`}>{miniProjectStatus}</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon blue">⭐</div>
                                    <div className="stat-info">
                                        <h3>Major Project</h3>
                                        <p className={`status ${majorProjectStatus.toLowerCase().replace(' ', '-')}`}>{majorProjectStatus}</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon green">🏆</div>
                                    <div className="stat-info">
                                        <h3>Certificates</h3>
                                        <p>0 Earned</p>
                                    </div>
                                </div>
                            </div>

                            <div className="projects-section">
                                <div className="section-header">
                                    <h2>My Projects</h2>
                                </div>

                                <div className="projects-grid">
                                    {projects?.miniProject && (
                                        <ProjectCard
                                            title="Mini Project"
                                            projectData={projects.miniProject}
                                            onClick={() => handleProjectClick(projects.miniProject)}
                                        />
                                    )}

                                    {projects?.majorProject && (
                                        <ProjectCard
                                            title="Major Project"
                                            projectData={projects.majorProject}
                                            onClick={() => handleProjectClick(projects.majorProject)}
                                        />
                                    )}
                                </div>

                                {!projects?.miniProject && !projects?.majorProject && (
                                    <div className="empty-state">
                                        <p>No projects assigned yet. Please contact your administrator.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}

export default StudentDashboard;
